// Pure helpers for the hh-no-primary issue's decide flow.
//
// Baker 2026-07-31 — the rail now renders the household's members and
// the pick IS the action ("verify" carrying primary_contact_id), which
// demotes every other claimant in the same step. Before this, the rail's
// only advice was "open the household record and pick a member", and for
// the MULTI-PRIMARY variant of this issue that advice could not work:
// the household's Edit form already named the right person, so no save
// the steward could make changed anything, while the second member kept
// household_role='primary' and the item kept asking. Baker, on the Brett
// Taylor household: "it says that one is the primary, but I don't know
// how to make it not the primary."
//
// The honest terminal escape ("We don't know who the primary contact
// is") stays — it writes a durable unknown marker (migration 0202) so
// nobody is asked again.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/.

export interface PrimaryIssueLike {
  detectorSlug: string;
}

export function isPrimaryIssue(issue: PrimaryIssueLike): boolean {
  return issue.detectorSlug === "hh-no-primary";
}

export const PRIMARY_UNKNOWN_LABEL = "We don't know who the primary contact is";

export function primaryUnknownConsequence(): string {
  return "Records that nobody knows who the primary contact of this household is — the item resolves for the whole team and it won't be asked again. Set a primary on the household record any time to supersede this. Reversible for 60 seconds (undo clears the answer and reopens the item).";
}

/** A live household member as the rail's picker needs it. */
export interface PrimaryCandidate {
  id: string;
  name: string;
  /** True when `households.primary_contact_id` already points here. */
  isPointer: boolean;
  /** True when this member carries household_role='primary'. */
  claimsPrimary: boolean;
}

/** The picked contact id is the whole action — block Apply without one. */
export function isValidPrimaryPick(
  contactId: string,
  candidates: readonly PrimaryCandidate[],
): boolean {
  const picked = contactId.trim();
  if (picked === "") return false;
  return candidates.some((cand) => cand.id === picked);
}

/**
 * The two shapes of this issue read very differently to a human, and
 * conflating them is what sent Baker hunting. "No primary at all" is a
 * blank to fill. "Several members all marked primary" is a contradiction
 * to resolve — and crucially, the household may ALREADY point at the
 * right person, so the answer is usually "confirm the one that's right",
 * not "change it".
 */
export function primaryPickPrompt(candidates: readonly PrimaryCandidate[]): string {
  const claimants = candidates.filter((cand) => cand.claimsPrimary);
  if (claimants.length < 2) {
    return "Pick which member is the primary contact. The others stay regular members.";
  }
  const pointer = claimants.find((cand) => cand.isPointer);
  if (pointer) {
    return `${claimants.length} members are marked as the primary contact, and the household currently lists ${pointer.name}. Confirm ${pointer.name} — or pick someone else. Whoever you choose becomes the only primary; the rest become regular members.`;
  }
  return `${claimants.length} members are marked as the primary contact. Pick the one that's right — the rest become regular members.`;
}

/**
 * Plain-English before→after for the chosen pick (UX rule #3 — human-
 * language impact, not a field diff). Names who is demoted, because the
 * demotion is the part the steward can't see coming.
 */
export function primaryPickConsequence(
  contactId: string,
  candidates: readonly PrimaryCandidate[],
): string {
  const picked = candidates.find((cand) => cand.id === contactId.trim());
  if (!picked) {
    return "Pick which member is the primary contact.";
  }
  const demoted = candidates.filter(
    (cand) => cand.id !== picked.id && cand.claimsPrimary,
  );
  const base = `${picked.name} becomes this household's primary contact — the one we route mail, statements, and household-level communication to.`;
  if (demoted.length === 0) {
    return `${base} Reversible for 60 seconds (undo restores the previous primary and reopens the item).`;
  }
  const names = formatNameList(demoted.map((cand) => cand.name));
  return `${base} ${names} ${demoted.length === 1 ? "stops being" : "stop being"} marked primary and ${demoted.length === 1 ? "becomes a" : "become"} regular ${demoted.length === 1 ? "member" : "members"} of the household — nothing else about ${demoted.length === 1 ? "their" : "their"} record changes. Reversible for 60 seconds (undo restores every previous role and reopens the item).`;
}

/** Apply-button label — names the choice, never "Apply". */
export function primaryPickApplyLabel(
  contactId: string,
  candidates: readonly PrimaryCandidate[],
): string {
  const picked = candidates.find((cand) => cand.id === contactId.trim());
  if (!picked) return "Set the primary contact (pick a member first)";
  // The re-affirm case gets its own words. "Make X the primary" reads as
  // a no-op when the screen already says X is the primary — which is
  // precisely the confusion this whole change exists to remove.
  const others = candidates.filter(
    (cand) => cand.id !== picked.id && cand.claimsPrimary,
  );
  if (picked.isPointer && others.length > 0) {
    return `Confirm ${picked.name} as the only primary & resolve`;
  }
  return `Make ${picked.name} the primary contact & resolve`;
}

/** "Ann", "Ann and Bob", "Ann, Bob, and Cal". */
function formatNameList(names: readonly string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}
